/**
 * @module builder
 */
import type { QuoteProduct } from 'paintscout'
import type { EditableRate, Product } from '../../../options'
import {
  getCoverageUnitFamily,
  getRateDefaultProductCoverage
} from '../../../options/product-coverage/product-coverage'
import type { CoverageUnitFamily } from '../../../options/product-coverage/product-coverage'
// Deep import rather than the area-substrates barrel, which reaches back into calculate-quote.
// These two builders are pure, and keeping them free of that edge is what lets the tier swap run
// inside calculateQuote itself (see swap-tier-products).
import { calculateMaterial } from '../../area-substrates/calculate-material'
import type { UpdateableProduct } from '../../area-substrates'

/**
 * What a swap knows about the slot beyond the slot itself. The Swap Products dialog and the tier swap
 * both pass both fields; `rate` is null when the caller has no rates document (the customer view, or
 * a server-side recalculation). Without a context every coverage comes from `matched`.
 */
export interface SwapCoverageContext {
  /** Library entry of the product being replaced. null/undefined when it is no longer in the library. */
  previous?: Product | null
  /** The slot's rate as configured in the rates document, for its per-product coverage. */
  rate?: Pick<EditableRate, 'defaultProducts' | 'rateType'> | null
}

const COVERAGE_FIELDS: {
  value: 'coverage_rate_value' | 'lnft_coverage_rate_value' | 'quantity_coverage_rate_value'
  override: 'coverage_rate_override' | 'lnft_coverage_rate_override' | 'quantity_coverage_rate_override'
  overrideValue:
    | 'coverage_rate_override_value'
    | 'lnft_coverage_rate_override_value'
    | 'quantity_coverage_rate_override_value'
  library: 'coverage' | 'lnftCoverage' | 'quantityCoverage'
  family: CoverageUnitFamily
}[] = [
  {
    value: 'coverage_rate_value',
    override: 'coverage_rate_override',
    overrideValue: 'coverage_rate_override_value',
    library: 'coverage',
    family: 'square'
  },
  {
    value: 'lnft_coverage_rate_value',
    override: 'lnft_coverage_rate_override',
    overrideValue: 'lnft_coverage_rate_override_value',
    library: 'lnftCoverage',
    family: 'linear'
  },
  {
    value: 'quantity_coverage_rate_value',
    override: 'quantity_coverage_rate_override',
    overrideValue: 'quantity_coverage_rate_override_value',
    library: 'quantityCoverage',
    family: 'quantity'
  }
]

function toNumber(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * The three coverage fields a swapped slot should carry (ENG-1954).
 *
 * A slot's coverage is not always the product's library coverage. A rate can configure a coverage
 * for its default product, and that value is baked into the slot when the surface is created with no
 * override flag set (convertProductDetails). Rebuilding the slot from the new product's library entry
 * therefore re-priced every such surface, even between two products whose library entries match.
 *
 * Per field:
 *  1. an overridden coverage keeps its override;
 *  2. a value that differs from the old product's library value was customized on the estimate (by
 *     the rate or by hand), so it stays;
 *  3. otherwise the rate's own coverage for the new product, for the one field its rate type uses;
 *  4. otherwise the new product's library value.
 * If the old product is gone from the library, step 2 cannot tell stock from custom, so any
 * non-zero slot value stays.
 */
export function resolveSwappedCoverage(
  matched: Product,
  existing: QuoteProduct | undefined,
  context: SwapCoverageContext
): Pick<
  QuoteProduct,
  | 'coverage_rate_value'
  | 'coverage_rate_override'
  | 'coverage_rate_override_value'
  | 'lnft_coverage_rate_value'
  | 'lnft_coverage_rate_override'
  | 'lnft_coverage_rate_override_value'
  | 'quantity_coverage_rate_value'
  | 'quantity_coverage_rate_override'
  | 'quantity_coverage_rate_override_value'
> {
  const { previous, rate } = context
  const rateFamily = rate?.rateType ? getCoverageUnitFamily(rate.rateType) : null
  const rateCoverage = rate ? getRateDefaultProductCoverage({ rate, productKey: matched.key }) : null

  const result = {} as ReturnType<typeof resolveSwappedCoverage>

  for (const field of COVERAGE_FIELDS) {
    const newDefault =
      rateCoverage !== null && rateFamily === field.family ? rateCoverage : toNumber(matched[field.library])

    let value = newDefault
    if (existing && !existing[field.override]) {
      const slotValue = toNumber(existing[field.value])
      // With no library entry to compare against, only a real stored value counts as customized; an
      // empty or 0 slot would otherwise price the new product at zero paint.
      const customized = previous ? slotValue !== toNumber(previous[field.library]) : slotValue > 0
      if (customized) {
        value = slotValue
      }
    }

    result[field.value] = value
    result[field.override] = !!existing?.[field.override]
    result[field.overrideValue] = existing?.[field.override] ? toNumber(existing[field.overrideValue]) : 0
  }

  return result
}

/**
 * resolveSwappedCoverage for the area editor's UpdateableProduct shape, used when one product on a
 * surface is changed to another (ENG-2147). Same rule as the Swap Products dialog, so a coverage the
 * rate baked into the slot, or one set by hand, carries over to the new product.
 */
export function resolveSwappedProductCoverage(
  matched: Product,
  existing: UpdateableProduct,
  context: SwapCoverageContext
): Pick<UpdateableProduct, 'coverage' | 'lnftCoverage' | 'quantityCoverage'> {
  const { coverage, lnftCoverage, quantityCoverage } = existing
  const slot = {
    coverage_rate_value: toNumber(coverage?.default),
    coverage_rate_override: !!coverage?.useCustom,
    coverage_rate_override_value: toNumber(coverage?.custom),
    lnft_coverage_rate_value: toNumber(lnftCoverage?.default),
    lnft_coverage_rate_override: !!lnftCoverage?.useCustom,
    lnft_coverage_rate_override_value: toNumber(lnftCoverage?.custom),
    quantity_coverage_rate_value: toNumber(quantityCoverage?.default),
    quantity_coverage_rate_override: !!quantityCoverage?.useCustom,
    quantity_coverage_rate_override_value: toNumber(quantityCoverage?.custom)
  } as QuoteProduct

  const resolved = resolveSwappedCoverage(matched, slot, context)

  return {
    coverage: {
      default: resolved.coverage_rate_value,
      useCustom: resolved.coverage_rate_override,
      custom: resolved.coverage_rate_override_value
    },
    lnftCoverage: {
      default: resolved.lnft_coverage_rate_value,
      useCustom: resolved.lnft_coverage_rate_override,
      custom: resolved.lnft_coverage_rate_override_value
    },
    quantityCoverage: {
      default: resolved.quantity_coverage_rate_value,
      useCustom: resolved.quantity_coverage_rate_override,
      custom: resolved.quantity_coverage_rate_override_value
    }
  }
}

/**
 * Build a raw QuoteProduct (area-substrate shape) from a matched product library entry.
 *
 * `existing` is the slot the new product replaces. Everything a product library entry can describe
 * comes from `matched`, but `coats` describes the *slot* rather than the product — the product
 * library has no coats field at all. Dropping it silently re-prices the slot at the substrate's coat
 * count, so two products with identical coverage and price produce different totals after a swap
 * (#23899). Volume is deliberately not carried here: every caller re-applies its own `volume*` fields
 * because an overridden volume must survive while a calculated one is recomputed.
 *
 * With a `coverageContext`, coverage follows resolveSwappedCoverage instead of the library (ENG-1954).
 */
export function buildRawQuoteProduct(
  matched: Product,
  existing?: QuoteProduct,
  coverageContext?: SwapCoverageContext
): QuoteProduct {
  return {
    product: {
      label: matched.label,
      name: matched.key,
      unitLabel: matched.unitLabel || 'gal',
      color: '',
      manufacturer: matched.manufacturer || ''
    },
    calculate: true,
    coverage_rate_value: matched.coverage || 0,
    coverage_rate_override: false,
    coverage_rate_override_value: 0,
    lnft_coverage_rate_value: matched.lnftCoverage || 0,
    lnft_coverage_rate_override: false,
    lnft_coverage_rate_override_value: 0,
    quantity_coverage_rate_value: matched.quantityCoverage || 0,
    quantity_coverage_rate_override: false,
    quantity_coverage_rate_override_value: 0,
    ...(coverageContext ? resolveSwappedCoverage(matched, existing, coverageContext) : {}),
    volume: 0,
    volume_override: false,
    volume_override_value: 0,
    price: matched.price || 0,
    price_override: false,
    price_override_value: 0,
    totalPrice: 0,
    totalPrice_override: false,
    totalPrice_override_value: 0,
    custom: false,
    // Spread last and only when set, so a slot with no override stays free of a `coats` key and
    // keeps following the substrate. A stored 0 counts as absent, not as "zero coats": the coats
    // dropdown already reads it back as Default (`product.coats ? String(product.coats) : ''`) and
    // getRateDefaultProductCoats discards it the same way. Carrying a 0 forward would price the
    // rebuilt slot at zero volume, since parseNumber(0, 1) returns 0.
    ...(existing?.coats ? { coats: existing.coats } : {})
  }
}

/**
 * Build a line-item material (UpdateableProduct shape) from a matched product library entry.
 *
 * Line-item materials use OverridableValue fields (coverage/price/totalPrice/etc.), unlike the
 * area-substrate QuoteProduct shape above. Emitting the wrong shape here (e.g. a raw number for
 * `price` or a missing `totalPrice`) causes the recalculation pipeline to throw — see
 * generate-product-description reading `material.totalPrice.default`. We mirror how the
 * "Change Product" dialog (MaterialsTable) builds a material and run it through calculateMaterial.
 */
export function buildLineItemMaterial(matched: Product, existing?: UpdateableProduct): UpdateableProduct {
  return calculateMaterial({
    ...matched,
    color: existing?.color ?? '',
    quantity: existing?.quantity ?? 1,
    coverage: { default: matched.coverage ?? 0, useCustom: false, custom: 0 },
    lnftCoverage: { default: matched.lnftCoverage ?? 0, useCustom: false, custom: 0 },
    quantityCoverage: { default: matched.quantityCoverage ?? 0, useCustom: false, custom: 0 },
    volume: { default: 0, useCustom: false, custom: 0 },
    price: { default: matched.price ?? 0, useCustom: false, custom: 0 },
    totalPrice: { default: 0, useCustom: false, custom: 0 }
  })
}
